import { NewPurchaseOrder, defaultPatron, initialState } from 'state/handsell';

import { NO_RESTRICTIONS } from 'components/EmailPolicyChoice';
import { OptionData } from 'selectors/handsellSelectors';
import { core } from '@tock/coretypes';
import {
  filter,
  find,
  flatMap,
  forEach,
  map,
  min,
  some,
  sumBy,
  toNumber,
  toString,
  uniqWith,
} from 'es-toolkit/compat';
import { filterNilValues } from '@tock/shared/utils/typeUtils';

const getOptions = (ticketType: core.ITicketType): core.ITicketOption[] => {
  return flatMap(
    ticketType.optionGroup,
    (optionGroup) => optionGroup.ticketOption as core.ITicketOption[]
  );
};

const getOptionGroups = (ticketType: core.ITicketType): core.ITicketOptionGroup[] => {
  return ticketType.optionGroup as core.ITicketOptionGroup[];
};

export const getOptionsForHandsell = (
  ticketGroup: core.TicketGroup | undefined,
  purchase: core.PastPurchase | undefined,
  ticketType: core.TicketType | undefined,
  ticketTypeTicketOptions: core.TicketOption[],
  allTicketTypes?: core.TicketType[],
  isTablesideModal?: boolean
): OptionData => {
  // Don't use allTicketTypes in normalized inventory mode
  const isUsingNormalizedOptions = ticketGroup?.hasNormalizedOptions;

  const isTableSide =
    purchase?.originatedAt === 'VISIT_EDIT' ||
    purchase?.originatedAt === 'VISIT_EDIT_DASHBOARD' ||
    !!purchase?.visitEditToken ||
    isTablesideModal;
  const tableSideTicketType = find(allTicketTypes, (type) => type.variety === 'TABLESIDE');

  const relevantTicketTypes = isUsingNormalizedOptions
    ? [ticketType, tableSideTicketType].filter(filterNilValues)
    : allTicketTypes;

  const allTicketOptions = flatMap(relevantTicketTypes, getOptions);
  const allTicketOptionGroups = flatMap(relevantTicketTypes, getOptionGroups);

  const [baseTicketOptions, basePriceMapByBackingMode] = processBaseTicketOptions(
    ticketGroup,
    ticketType,
    allTicketOptions,
    ticketTypeTicketOptions
  );

  // splice in tableside options if tableside
  const [tablesideTicketOptions, tablesidePriceMapByBackingMode] = processTablesideOptions(
    isTableSide,
    tableSideTicketType,
    basePriceMapByBackingMode
  );

  // add/overwrite prices to grandfather them in from the purchase when editing
  const [purchasedTicketOptions, purchasedPriceMayByBackingMode] = processPurchaseOptions(
    purchase,
    ticketGroup,
    tablesidePriceMapByBackingMode,
    { sourceFromPurchase: true, baseTicketOptions: tablesideTicketOptions }
  );

  // consolidate built up ticket options from different sources
  const ticketOptions = uniqWith(
    [...baseTicketOptions, ...tablesideTicketOptions, ...purchasedTicketOptions],
    (ticketOptionA, ticketOptionB) =>
      ticketOptionA.id === ticketOptionB.id &&
      ticketOptionA.backingMode === ticketOptionB.backingMode &&
      ticketOptionA.ticketOptionGroupId === ticketOptionB.ticketOptionGroupId
  );

  // flatten built up prices
  const optionPrices: core.IPrice[] = flatMap(
    purchasedPriceMayByBackingMode,
    (priceMap: Dictionary<number>, backingMode: string) => {
      return map(priceMap, (amount: number, key: string) => ({
        id: toNumber(key),
        amount,
        backingMode: backingMode as core.BackingMode,
      }));
    }
  );

  // build groups from consolidated ticket options
  const ticketOptionGroups = uniqWith(
    filter(
      [...allTicketOptionGroups, ...(purchase?.archivedPurchasedTicketOptionGroup || [])],
      (group) => some(ticketOptions, (option) => option.ticketOptionGroupId === group.id)
    ),
    (ticketOptionGroupA, ticketOptionGroupB) =>
      ticketOptionGroupA.id === ticketOptionGroupB.id &&
      ticketOptionGroupA.backingMode === ticketOptionGroupB.backingMode &&
      ticketOptionGroupA.parentTicketOptionId === ticketOptionGroupB.parentTicketOptionId
  );

  return {
    ticketOptions,
    ticketOptionGroups,
    optionPrices,
  };
};

interface ProcessPurchaseOptionsNewMode {
  sourceFromPurchase: true;
  baseTicketOptions: core.ITicketOption[];
}

/**
 * Generates Ticket Options from the purchased options on a purchase for "grandfathering" in available options on a purchase,
 * and sets their prices in the price map. This must be done last so the purchase prices take precedence over all others
 *
 * @param purchase
 * @param ticketGroup
 * @param priceMapByBackingMode
 * @param allTicketOptions
 *
 * @returns [purchased option ticket options, accumulated price map]
 */
function processPurchaseOptions(
  purchase: core.PastPurchase | undefined,
  ticketGroup: core.TicketGroup | undefined,
  priceMapByBackingMode: Dictionary<Dictionary<number>>,
  processingMode: ProcessPurchaseOptionsNewMode
): [core.ITicketOption[], Dictionary<Dictionary<number>>] {
  if (purchase && ticketGroup) {
    // Overwrite using purchased option prices.
    forEach(purchase.purchasedOption, (option) => {
      priceMapByBackingMode[option.backingMode || 'TICKET_OPTION'][toString(option.id ?? '')] =
        option.amount ?? 0;
      forEach(option.modifierOption, (modifier) => {
        priceMapByBackingMode[modifier.backingMode || 'TICKET_OPTION'][
          toString(modifier.id ?? '')
        ] = modifier.amount ?? 0;
      });
    });
  }

  // source the purchased ticket options, with the specific groups they were purchased on
  const purchasedTicketOptionIds = new Set(
    purchase?.purchasedOption.map(
      (purchasedOption) =>
        `${purchasedOption.id}-${purchasedOption.ticketOptionGroupId}-${purchasedOption.backingMode}`
    )
  );

  const purchasedTicketOptions: core.ITicketOption[] = [
    // overwrite the base ticket option data with purchase ticket option data
    ...processingMode.baseTicketOptions.filter(
      (ticketOption) =>
        !purchasedTicketOptionIds.has(
          `${ticketOption.id}-${ticketOption.ticketOptionGroupId}-${ticketOption.backingMode}`
        )
    ),
    ...(purchase?.archivedPurchasedTicketOption || []),
  ];
  return [purchasedTicketOptions, priceMapByBackingMode];
}

/**
 * Generates ticket options from the tableside ticket type, if relevant, and accumulates the tableside ticket type prices into the price map
 *
 * @param isTableSide
 * @param tableSideTicketType
 * @param priceMapByBackingMode
 *
 * @returns [tableside specific ticket options, accumulated price map]
 */
function processTablesideOptions(
  isTableSide: boolean | undefined,
  tableSideTicketType: core.TicketType | undefined,
  priceMapByBackingMode: Dictionary<Dictionary<number>>
): [core.ITicketOption[], Dictionary<Dictionary<number>>] {
  if (isTableSide && tableSideTicketType) {
    const tablesideTicketOptions = getOptions(tableSideTicketType).filter(
      (option) => !option.isArchived
    );
    forEach(tablesideTicketOptions, (option) => {
      priceMapByBackingMode[option.backingMode || 'TICKET_OPTION'][toString(option.id ?? '')] =
        option.defaultPrice ?? 0;
      tablesideTicketOptions.push(option);
    });
    return [tablesideTicketOptions, priceMapByBackingMode];
  }
  return [[], priceMapByBackingMode];
}

/**
 * Generates the base ticket options and prices for handsell
 * Uses the ticketGroup if in non-normalized world, or the ticket type in the normalized world
 *
 * @param ticketGroup
 * @param ticketType
 * @param allTicketOptions
 * @param ticketTypeTicketOptions
 *
 * @returns [base ticket options, accumulated price map]
 */
function processBaseTicketOptions(
  ticketGroup: core.TicketGroup | undefined,
  ticketType: core.TicketType | undefined,
  allTicketOptions: core.ITicketOption[],
  ticketTypeTicketOptions: core.TicketOption[]
): [core.ITicketOption[], Dictionary<Dictionary<number>>] {
  const priceMapByBackingMode: Dictionary<Dictionary<number>> = {
    TICKET_OPTION: {},
    MENU_ITEM: {},
    MENU_MODIFIER: {},
  };

  const isUsingNormalizedOptions = ticketGroup?.hasNormalizedOptions;

  // get the "base" ticket options and prices, sourced from either the ticket group in non-normalized world or the ticket type in normalized world
  if (ticketGroup && !isUsingNormalizedOptions && ticketGroup.ticketTypeId === ticketType?.id) {
    forEach(ticketGroup.option, (price) => {
      priceMapByBackingMode[price.backingMode || 'TICKET_OPTION'][toString(price.id ?? '')] =
        price.amount ?? 0;
    });
    const ticketGroupOptionIds = new Set(ticketGroup.option.map((option) => option.id));
    const baseTicketOptions = filter(allTicketOptions, (ticketOption) =>
      ticketGroupOptionIds.has(ticketOption.id)
    );
    return [baseTicketOptions, priceMapByBackingMode];
  } else {
    forEach(ticketTypeTicketOptions, (option) => {
      if (!option.isArchived) {
        priceMapByBackingMode[option.backingMode || 'TICKET_OPTION'][toString(option.id ?? '')] =
          option.defaultPrice ?? 0;
      }
    });
    // both backing mode (add-on and menus) options are encoded onto the ticket type for editing purposes, but should not all be included in the "base"
    const baseTicketOptions = ticketTypeTicketOptions.filter(
      (ticketOption) =>
        (ticketType?.isUsingMenus
          ? ticketOption.backingMode !== 'TICKET_OPTION'
          : ticketOption.backingMode === 'TICKET_OPTION') && !ticketOption.isArchived
    );

    return [baseTicketOptions, priceMapByBackingMode];
  }
}

const purchaseOrderHelpers = {
  /**
   * Generates feeOrders from the ticketGroup.
   * @param {Object} ticketGroup The ticket group object.
   * @param {Object} purchase The existing purchase object.
   * @returns {Array} List of FeeOrder messages.
   */
  constructFeeOrders(ticketGroup: core.ITicketGroup, purchase?: core.PastPurchase) {
    const feeOrders: core.IFeePrice[] = [];
    // If there is already a purchase associated with the ticket group, continue with
    // that purchase fees.
    if (purchase) {
      // Construct the fee order based on what it was at time of original purchase
      if (!purchase.fee) {
        return feeOrders;
      }
      purchase.fee.forEach((fee: core.IFeePrice) => {
        feeOrders.push(fee);
      });
      return feeOrders;
    }
    if (!ticketGroup.fee) {
      return feeOrders;
    }
    ticketGroup.fee.forEach((fee) => {
      feeOrders.push(fee);
    });
    return feeOrders;
  },

  getMaxComp(purchaseDetail: core.PastPurchase): number {
    const totalSubtotalDiscount = filter(purchaseDetail?.discount ?? [], (discount) => {
      return discount.discountType === 'SUBTOTAL_COMP';
    });
    const totalPostTotalDiscount = filter(purchaseDetail?.discount ?? [], (discount) => {
      return discount.discountType !== 'SUBTOTAL_COMP';
    });

    const totalSubtotalDiscountCents = sumBy(
      totalSubtotalDiscount,
      (discount) => discount?.amountCents ?? 0
    );
    const totalPostTotalDiscountCents = sumBy(
      totalPostTotalDiscount,
      (discount) => discount?.amountCents ?? 0
    );

    const existingComp = purchaseDetail.compAmountCents || 0;
    const baseSubTotal = purchaseDetail.subtotal - existingComp - totalSubtotalDiscountCents;
    const onePlusRate = (purchaseDetail.tax + purchaseDetail.serviceCharge) / baseSubTotal + 1;

    return baseSubTotal - totalPostTotalDiscountCents / onePlusRate;
  },

  /**
   * Return the maximum post total comp allowed for this purchase
   */
  getMaxPostTotalComp(purchaseDetail: core.PastPurchase): number {
    return purchaseDetail.totalPrice;
  },

  hasGiftCard(purchaseOrder: core.IPurchaseOrder) {
    const discounts = (purchaseOrder && purchaseOrder.discount) || [];
    return some(discounts, (discount) => discount.giftCard);
  },

  hasGiftCardWithNumber(purchaseOrder: NewPurchaseOrder, giftCardNumber: string) {
    const discounts = (purchaseOrder && purchaseOrder.discount) || [];
    return this.isGiftCardWithNumberInDiscounts(discounts, giftCardNumber);
  },

  isGiftCardWithNumberInDiscounts(discounts: core.IDiscount[], giftCardNumber: string) {
    return some(
      discounts,
      (discount) =>
        discount.giftCard &&
        discount.giftCard.externalIdentifier === giftCardNumber &&
        !discount.confirmationNumber
    );
  },

  initialNewPurchaseOrder(options: {
    ticketGroup: core.TicketGroup;
    dateTime?: string;
    ticketSubsetId?: number;
    patron?: core.Patron;
    numTickets?: number;
    tableIdsToBook?: number[];
    ticketTypeId?: number;
    seatingAreaId?: number;
  }): NewPurchaseOrder {
    const {
      ticketGroup,
      dateTime,
      ticketSubsetId,
      ticketTypeId,
      patron,
      numTickets,
      tableIdsToBook,
      seatingAreaId,
    } = options;
    const ticketCount = numTickets || min([ticketGroup.minPurchaseSize, numTickets]);

    return {
      ...initialState.newPurchaseOrder,
      bookingNote: ticketGroup.auditedBookingNote,
      dateTime: dateTime || ticketGroup.dateTime,
      patron: patron || { ...defaultPatron },
      dinerPatron: [patron || { ...defaultPatron }],
      adminOverride: true,
      ticketGroupId: ticketGroup.id,
      ticketTypePrice: ticketGroup.type[0].amount,
      ticketTypeId: ticketTypeId || ticketGroup.type[0].id,
      ticketSubsetId: ticketSubsetId,
      ticketCount,
      increasePurchaseSizeTo: ticketCount,
      fee: purchaseOrderHelpers.constructFeeOrders(ticketGroup),
      emailPolicy: NO_RESTRICTIONS,
      tableId: tableIdsToBook || [],
      excludeFromPacing: false,
      seatingAreaId,
    };
  },
};

interface KeyValue {
  attribute: string;
  attributeValue: string;
}

export function buildPurchaseOrderKeyValue(attributeObject: Dictionary<string>): KeyValue[] {
  return Object.keys(attributeObject).map((key) => ({
    attribute: key,
    attributeValue: attributeObject[key],
  }));
}

export default purchaseOrderHelpers;
